import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Dropdown, DropdownProps } from '@usepepper/ui/atoms/dropdown';
import {
  FlatList,
  Platform,
  StyleProp,
  TextInput,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { SearchInput } from '@usepepper/ui/atoms/search-input';

const MAX_DROPDOWN_ITEMS = 5;
const STATIC_CONTENT_HEIGHT = 56;
const DEFAULT_MAX_DROPDOWN_HEIGHT = 270;

export type SearchableDropdownMenuItemData<T> = T & {
  key: string;
};

export interface SearchableDropdownMenuRef {
  focusSearchInput: () => void;
  scrollToIndex: (index: number) => void;
}

export type SearchableDropdownMenuProps<T> = Omit<DropdownProps, 'children'> & {
  dropdownContainerOverrides?: StyleProp<ViewStyle>;
  estimatedListItemHeight?: number;
  focusedIndex?: number | null;
  // Rendered above the search input — use this for actions (e.g. 'New Item'button)
  headerSlot?: React.ReactNode;
  listData: SearchableDropdownMenuItemData<T>[];
  listEmptyComponent: React.ReactElement;
  listFooterComponent?: React.ReactElement | null;
  onEndReached?: () => void;
  onFocusedIndexChange?: (index: number | null) => void;
  onSearch: (text: string) => void;
  placeholderText: string;
  renderItem: (
    item: SearchableDropdownMenuItemData<T>,
    index?: number,
    isFocused?: boolean,
  ) => React.ReactElement;
  searchText: string;
};

function SearchableDropdownMenuInner<T>(
  {
    listData,
    searchText,
    placeholderText,
    onSearch,
    listEmptyComponent,
    renderItem,
    estimatedListItemHeight,
    dropdownContainerOverrides,
    focusedIndex = null,
    headerSlot,
    listFooterComponent,
    onEndReached,
    onFocusedIndexChange,
    onClose,
    ...dropdownProps
  }: SearchableDropdownMenuProps<T>,
  ref: React.Ref<SearchableDropdownMenuRef>,
) {
  const [styles] = useStyles();
  const listRef = useRef<FlatList<SearchableDropdownMenuItemData<T>>>(null);
  const searchInputRef = useRef<TextInput>(null);

  // Flag to prevent dropdown from closing when SearchInput handles Escape
  const justHandledEscapeRef = useRef(false);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    focusSearchInput: () => searchInputRef.current?.focus(),
    scrollToIndex: (index: number) => {
      if (listRef.current && index >= 0 && index < listData.length) {
        const itemHeight = estimatedListItemHeight || STATIC_CONTENT_HEIGHT;
        listRef.current.scrollToIndex({
          index,
          viewOffset: itemHeight / 2,
        });
      }
    },
  }));

  const handleSearchEscapePress = () => {
    justHandledEscapeRef.current = true;
    searchInputRef.current?.blur();
  };

  const handleSearchKeyPress = (e: { nativeEvent: { key: string } }) => {
    if (e.nativeEvent.key === Key.Escape) {
      handleSearchEscapePress();
    }

    if (e.nativeEvent.key === Key.ArrowDown) {
      searchInputRef.current?.blur();
      let nextIndex = 0;

      if (focusedIndex !== null) {
        nextIndex = Math.min(listData.length - 1, focusedIndex + 1);
      }

      onFocusedIndexChange?.(nextIndex);
    }
  };

  const handleClose = useCallback(() => {
    // If SearchInput just handled Escape, don't close - it just blurred the input
    if (justHandledEscapeRef.current) {
      justHandledEscapeRef.current = false;
      return;
    }
    onClose();
  }, [onClose]);

  const handleOnScrollToIndexFailed = useCallback(
    (info: { index: number }) => {
      // if we fail to scroll to the index, try again after a short delay
      setTimeout(() => {
        if (
          listRef.current &&
          info.index >= 0 &&
          info.index < listData.length
        ) {
          const itemHeight = estimatedListItemHeight || STATIC_CONTENT_HEIGHT;
          listRef.current.scrollToIndex({
            index: info.index,
            viewOffset: itemHeight / 2,
          });
        }
      }, 100);
    },
    [listData.length, estimatedListItemHeight],
  );

  // Reset focus when list data changes
  useEffect(() => {
    onFocusedIndexChange?.(null);
  }, [
    listData.length,
    searchText,
    dropdownProps.isVisible,
    onFocusedIndexChange,
  ]);

  return (
    <Dropdown
      containerStyle={[
        styles.dropdownContainerOverrides,
        estimatedListItemHeight
          ? {
              maxHeight:
                estimatedListItemHeight * MAX_DROPDOWN_ITEMS +
                STATIC_CONTENT_HEIGHT,
            }
          : { maxHeight: DEFAULT_MAX_DROPDOWN_HEIGHT },
        dropdownContainerOverrides,
      ]}
      {...dropdownProps}
      onClose={handleClose}
    >
      {headerSlot}
      <TouchableWithoutFeedback>
        <View collapsable={false}>
          <SearchInput
            onKeyPress={handleSearchKeyPress}
            onSearch={onSearch}
            placeholderText={placeholderText}
            ref={searchInputRef}
            searchText={searchText}
          />
        </View>
      </TouchableWithoutFeedback>
      <FlatList<SearchableDropdownMenuItemData<T>>
        data={listData}
        keyExtractor={(item) => item.key}
        ListEmptyComponent={listEmptyComponent}
        ListFooterComponent={listFooterComponent}
        maxToRenderPerBatch={15}
        onEndReached={onEndReached}
        onEndReachedThreshold={0.5}
        onScrollToIndexFailed={handleOnScrollToIndexFailed}
        ref={listRef}
        renderItem={({ item, index }) =>
          renderItem(item, index, focusedIndex === index)
        }
      />
    </Dropdown>
  );
}

// forwardRef with generics requires this pattern
export const SearchableDropdownMenu = forwardRef(
  SearchableDropdownMenuInner,
) as <T>(
  props: SearchableDropdownMenuProps<T> & {
    ref?: React.Ref<SearchableDropdownMenuRef>;
  },
) => React.ReactElement;

const useStyles = createStyles((theme) => ({
  dropdownContainerOverrides: {
    gap: 8,
    marginTop: 1,
    padding: 8,
    ...Platform.select({
      web: {
        scrollbarColor: `${theme.sk.color.bg.scrollbar} transparent`,
        scrollbarWidth: 'thin',
      },
    }),
  },
}));
